import httpos from "./httpos";

const PENDING_SUBMISSION_KEY = "pendingPropertyExportSubmission";

function newIdempotencyKey() {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Keyed by payload signature rather than holding a single record. One record
// meant a second submission - another tab, or a regeneration started from
// Saved Exports - overwrote the first's key, so retrying the first generated a
// new key and bought its contacts a second time.
function pendingSubmissions() {
  try {
    const stored = JSON.parse(localStorage.getItem(PENDING_SUBMISSION_KEY) || "{}");
    return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
  } catch {
    return {};
  }
}

/**
 * One key per logical submission, surviving a reload.
 *
 * A submission that fails ambiguously - the request left, the response did not
 * come back - must retry under the same key, or the retry buys the contacts a
 * second time. The key is tied to the payload, so a genuinely different export
 * never reuses it.
 */
export function idempotencyKeyFor(payload) {
  const signature = JSON.stringify(payload);
  const pending = pendingSubmissions();
  if (typeof pending[signature] === "string") {
    return pending[signature];
  }

  const key = newIdempotencyKey();
  // Bounded: a submission that never completes would otherwise leave its entry
  // behind for good. Oldest first, so the newest submissions are the ones kept.
  const entries = [...Object.entries(pending), [signature, key]].slice(-5);
  localStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify(Object.fromEntries(entries)));
  return key;
}

/**
 * Called once a submission is known to have landed, so the next one is new.
 *
 * Only this payload's entry goes: another tab may still be mid-submission with
 * one of its own, and clearing that would hand its retry a fresh key.
 */
export function clearPendingSubmission(payload) {
  const pending = pendingSubmissions();
  if (payload !== undefined) {
    delete pending[JSON.stringify(payload)];
    localStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify(pending));
    return;
  }
  localStorage.removeItem(PENDING_SUBMISSION_KEY);
}

export async function fetchExportOptions() {
  const { data } = await httpos.get("exports/options");
  return data;
}

export async function fetchExportPreview(parcelIds, page = 1, perPage = 100) {
  const { data } = await httpos.post("exports/preview", {
    parcel_ids: parcelIds,
    page,
    per_page: perPage,
  });
  return data;
}

export async function fetchExportCreditPreview(parcelIds, columns) {
  const { data } = await httpos.post("exports/credit-preview", {
    parcel_ids: parcelIds,
    columns,
  });
  return data;
}

export async function createPropertyExport(payload, idempotencyKey) {
  const { data } = await httpos.post("exports", payload, {
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return data;
}

/**
 * The modern export request that reproduces a saved legacy export.
 *
 * Returns a payload rather than starting anything, so regeneration goes through
 * the same creation endpoint - and the same validation, credit forecast and
 * idempotency - as an export requested from the search page.
 */
export async function fetchLegacyExportPlan(id) {
  const { data } = await httpos.get(`exports/legacy/${id}`);
  return data;
}

export async function fetchPropertyExport(id) {
  const { data } = await httpos.get(`exports/${id}`);
  return data;
}

export async function listPropertyExports(page = 1, perPage = 5) {
  const { data } = await httpos.get("exports", {
    params: { page, per_page: perPage },
  });
  return data;
}

export async function fetchPropertyExportDownload(id) {
  const { data } = await httpos.get(`exports/${id}/download`);
  return data;
}
